// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, IconRefreshLine, Spinner, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
// Others
import { adminRoute } from '~/utils/routes/admin';
// Styles, images, icons
import styles from './Dashboard.module.scss';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { useEffect, useMemo } from 'react';
import { getProfile } from '~/thunks/user/userThunk';
import { teacherRoute } from '~/utils/routes/teacher';
import { studentRoute } from '~/utils/routes/student';

const cx = classNames.bind(styles);

const Dashboard = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['teacher/dashboard']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { profile: userProfile, isLoading, errorMessage } = useAppSelector((state) => state.userState);

  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Declare Memo
  const teacherName = useMemo(
    () => `${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`.trim(),
    [userProfile]
  );
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetProfile = async () => {
    await dispatch(getProfile()).unwrap();
  };

  const renderContent = () => {
    if (isLoading && !userProfile) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage && !userProfile) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' margin='medium 0'>
            <Button
              onClick={handleGetProfile}
              size='medium'
              color='primary'
              margin='x-large 0 0 0'
              renderIcon={<IconRefreshLine />}
            >
              {t('retry_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </div>
      );
    }

    if (!userProfile) {
      return <></>;
    }

    return (
      <>
        <Heading level='h3' margin='0 0 small 0'>
          {t('dashboard_title', { teacherName })}
        </Heading>

        <Heading level='h4' margin='large 0 small 0'>
          {t('dashboard_area_title')}
        </Heading>

        <View as='div' className={cx('content')}>
          <View as='div' className={cx('section')} onClick={handleNavigateStudentList}>
            <Heading level='h4'>{t('dashboard_student_list')}</Heading>
          </View>

          <View as='div' className={cx('section')} onClick={handleNavigateMaterialList}>
            <Heading level='h4'>{t('dashboard_material_list')}</Heading>
          </View>

          <View as='div' className={cx('section')} onClick={handleNavigateDiscussionForums}>
            <Heading level='h4'>{t('dashboard_discussion_forum')}</Heading>
          </View>

          <View as='div' className={cx('section')} onClick={handleNavigateStudentArea}>
            <Heading level='h4'>{t('dashboard_enter_student_area')}</Heading>
          </View>

          <View as='div' className={cx('section')} onClick={handleNavigateExamArea}>
            <Heading level='h4'>{t('dashboard_enter_exam_area')}</Heading>
          </View>
        </View>
      </>
    );
  };

  const handleNavigateStudentList = () => {
    navigate(`${teacherRoute.base}${teacherRoute.students}`);
  };

  const handleNavigateMaterialList = () => {
    navigate(`${teacherRoute.base}${teacherRoute.materials}`);
  };

  const handleNavigateDiscussionForums = () => {
    navigate(`${teacherRoute.base}${teacherRoute.discussions}`);
  };

  const handleNavigateStudentArea = () => {
    navigate(`${studentRoute.base}${studentRoute.dashboard}`);
  };

  const handleNavigateExamArea = () => {
    navigate(`${teacherRoute.base}${teacherRoute.examArea}`);
  };
  //#endregion Handle Function

  return <div className={cx('container')}>{renderContent()}</div>;
};

export default Dashboard;
